import { Injectable } from '@angular/core';
import {
  FebStatut,
  IFebActivite,
  IFicheExpressionBesoinResponse,
} from 'src/app/shared/models/fiche-expression-besoin';

@Injectable({
  providedIn: 'root'
})
export class FebPdfService {
  private readonly BLUE = '#0070AD';
  private readonly BLUE_DARK = '#00538A';
  private readonly GREY = '#4D4F53';
  private readonly LIGHT_GREY = '#9B9B9B';

  private pdfMakeInstance: any;

  async download(feb: IFicheExpressionBesoinResponse): Promise<void> {
    const logo = await this.getBase64ImageFromURL('assets/logo-capgemini.png');
    const docDefinition = this.buildDocDefinition(feb, logo);
    const pdfMake = await this.getPdfMake();

    pdfMake
      .createPdf(docDefinition)
      .download(`FEB-${this.sanitizeFileName(feb.febNumber ?? feb.febLabel ?? feb.febId)}.pdf`);
  }

  private async getPdfMake(): Promise<any> {
    if (this.pdfMakeInstance) {
      return this.pdfMakeInstance;
    }

    const [pdfMakeModule, pdfFontsModule] = await Promise.all([
      import('pdfmake/build/pdfmake'),
      import('pdfmake/build/vfs_fonts')
    ]);
    const pdfMake = (pdfMakeModule as any).default || pdfMakeModule;
    const fonts = pdfFontsModule as any;
    const vfs = fonts.default?.pdfMake?.vfs
      || fonts.pdfMake?.vfs
      || fonts.default?.vfs
      || fonts.vfs
      || fonts.default
      || fonts;

    if (!vfs['Roboto-Medium.ttf'] || !vfs['Roboto-Regular.ttf']) {
      throw new Error('pdfmake : les polices Roboto sont absentes de la VFS.');
    }

    pdfMake.vfs = vfs;
    this.pdfMakeInstance = pdfMake;
    return pdfMake;
  }

  private buildDocDefinition(feb: IFicheExpressionBesoinResponse, logo: string): any {
    const activitesBody = this.buildActivitesBody(feb.febActivites ?? []);
    return {
      pageMargins: [36, 36, 36, 40],
      defaultStyle: { fontSize: 9, color: this.GREY },
      styles: {
        h1: { fontSize: 24, bold: true, color: this.BLUE, characterSpacing: 0.5 },
        sectionTitle: { fontSize: 12, bold: true, color: this.BLUE, margin: [0, 18, 0, 6] },
        tableHeader: { fontSize: 8, bold: true, color: 'white', margin: [6, 5, 6, 5] },
        label: { fontSize: 7.5, bold: true, color: this.LIGHT_GREY },
      },
      content: [
        {
          columns: [
            {
              width: '*',
              text: feb.febNumber ?? 'FICHE D\'EXPRESSION DE BESOIN',
              style: 'h1',
              margin: [0, 6, 0, 0]
            },
            {
              image: logo,
              width: 110,
              alignment: 'right',
              margin: [0, 2, 0, 0]
            }
          ],
          columnGap: 10,
          margin: [0, 0, 0, 16]
        },
        {
          table: {
            widths: ['*'],
            body: [[
              {
                text: `${(feb.febProjectNom ?? 'PROJET').toUpperCase()}  >  ${(feb.febDemandeurNom ?? 'DEMANDEUR').toUpperCase()}`,
                color: this.BLUE,
                bold: true,
                fontSize: 11,
                margin: [8, 8, 8, 8]
              }
            ]]
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => this.BLUE,
            vLineColor: () => this.BLUE
          },
          margin: [0, 0, 0, 12]
        },
        this.infoGrid([
          { label: 'NUMERO FEB', value: feb.febNumber },
          { label: 'LABEL FEB', value: feb.febLabel },
          { label: 'STATUT', value: this.formatStatut(feb.febStatut) },
          { label: 'DEMANDEUR', value: feb.febDemandeurNom },
          { label: 'PILOTE DE LOT', value: feb.febPilotLotNom },
          { label: 'CREATION', value: this.formatDate(feb.createdAt) },
          { label: 'ENTITE SNCF', value: feb.febEntiteSncfNom },
          { label: 'DIVISION DEMANDEUSE', value: feb.febDivisionDemandeurNom },
          { label: 'MISE A JOUR', value: this.formatDate(feb.updatedAt) },
          { label: 'PROJET', value: feb.febProjectNom },
          { label: 'LOCALISATION', value: feb.febLocalisationLabel },
          { label: 'UO / ACTIVITE PRESSENTIE', value: feb.febUoActivitePressentiLabel },
          { label: 'NOMENCLATURE UO', value: feb.febUoNomenclatureLabel },
          { label: 'NOMENCLATURE', value: feb.febNomenclature },
          { label: 'PRESENCE SITE SNCF', value: feb.febPresenceSiteSncf },
          { label: 'PC SNCF FOURNI', value: feb.febPcSncf == null ? null : feb.febPcSncf ? 'Oui' : 'Non' },
          { label: 'PROVISION DEPLACEMENT & ASTREINTE', value: this.formatMontant(feb.febProvisionDeplacementAstreinte) },
          { label: 'DUREE PREVISIONNELLE', value: feb.febDuree == null ? null : `${feb.febDuree} j` },
          { label: 'DATE DEMANDE', value: this.formatDate(feb.febDateDemande) },
          { label: 'DATE DEMARRAGE', value: this.formatDate(feb.febDateDemarrage) },
          { label: 'DATE FIN PREVISIONNELLE', value: this.formatDate(feb.febDateFinPrevisionnelle) },
        ], 3),

        { text: 'CONTEXTE ET OBJECTIF', style: 'sectionTitle' },
        this.justifiedText(feb.febContexteObjectifProjet),

        { text: 'DESCRIPTION DU BESOIN', style: 'sectionTitle' },
        this.justifiedText(feb.febDescriptionBesoin),

        { text: 'ACTIVITES', style: 'sectionTitle' },
        {
          table: {
            widths: ['*', 100],
            headerRows: 1,
            body: [
              [
                { text: 'ACTIVITE', style: 'tableHeader', fillColor: this.BLUE },
                { text: 'REPARTITION', style: 'tableHeader', fillColor: this.BLUE_DARK, alignment: 'center' }
              ],
              ...activitesBody
            ]
          },
          layout: this.greyGrid(),
          margin: [0, 8, 0, 0]
        },
        { text: 'COMPETENCES REQUISES', style: 'sectionTitle', pageBreak: 'before' },
        this.infoGrid([
          { label: 'ANNEES D\'EXPERIENCE', value: feb.febNbAnneesExperiences },
          { label: 'COMPETENCES FONCTIONNELLES', value: feb.febCompetencesFonctionnelles },
          { label: 'COMPETENCES TECHNIQUES', value: feb.febCompetencesTechniques },
          { label: 'ENVIRONNEMENT TECHNIQUE', value: feb.febEnvironnementTechnique },
          { label: 'SAVOIR-ETRE ET QUALITES', value: feb.febSavoirEtreQualites },
        ], 2),

        {
          text: [
            { text: 'Document genere automatiquement le ' + new Date().toLocaleDateString('fr-FR') + ' : ', bold: true },
            'cette fiche recapitule l\'expression de besoin renseignee dans PackGemini.'
          ],
          fontSize: 8,
          italics: true,
          color: this.LIGHT_GREY,
          margin: [0, 16, 0, 0]
        }
      ]
    };
  }

  private buildActivitesBody(activites: IFebActivite[]): any[] {
    if (!activites.length) {
      return [[
        { text: 'Aucune activite renseignee', fontSize: 9, margin: [6, 5, 6, 5] },
        { text: '0%', fontSize: 9, bold: true, alignment: 'center', margin: [0, 5, 0, 5] }
      ]];
    }

    const rows = activites.map(activity => [
      { text: activity.label ?? '-', fontSize: 9, margin: [6, 5, 6, 5] },
      { text: `${activity.pourcentage ?? 0}%`, fontSize: 9, bold: true, alignment: 'center', margin: [0, 5, 0, 5] }
    ]);

    const total = activites.reduce((sum, activity) => sum + (activity.pourcentage ?? 0), 0);
    rows.push([
      { text: 'TOTAL', fontSize: 9, bold: true, alignment: 'left', margin: [6, 5, 6, 5] },
      { text: `${total}%`, fontSize: 9, bold: true, alignment: 'center', margin: [0, 5, 0, 5] }
    ]);
    return rows;
  }

  private infoGrid(fields: Array<{ label: string; value: unknown }>, perRow: number): any {
    const visible = fields.filter(field => field.value !== undefined);
    const body: any[] = [];

    for (let i = 0; i < visible.length; i += perRow) {
      const chunk = visible.slice(i, i + perRow);
      body.push(chunk.map(field => ({
        text: field.label,
        style: 'label',
        margin: [6, 7, 6, 0]
      })).concat(Array(perRow - chunk.length).fill({ text: '' })));
      body.push(chunk.map(field => ({
        text: this.toDisplayValue(field.value),
        fontSize: 10,
        bold: true,
        margin: [6, 1, 6, 7]
      })).concat(Array(perRow - chunk.length).fill({ text: '' })));
    }

    return {
      table: {
        widths: Array(perRow).fill(`${100 / perRow}%`),
        body
      },
      layout: {
        hLineWidth: (i: number) => (i % 2 === 0 && i !== 0 && i !== body.length ? 0.7 : 0),
        vLineWidth: () => 0,
        hLineColor: () => '#E5E5E5'
      }
    };
  }

  private justifiedText(text?: string | null): any {
    if (!text) {
      return { text: '-', fontSize: 9 };
    }

    const paragraphs = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => ({
        text: line,
        fontSize: 9,
        lineHeight: 1.25,
        alignment: 'justify',
        margin: [0, 0, 0, 6]
      }));
    return { stack: paragraphs };
  }

  private toDisplayValue(value: unknown): string {
    if (value == null || value === '') {
      return '—';
    }
    return String(value);
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
  }

  private formatMontant(value?: number | null): string {
    const amount = value ?? 0;
    return amount
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €';
  }

  private formatStatut(statut?: FebStatut | null): string {
    switch (statut) {
      case FebStatut.CREEE:
        return 'Creee';
      case FebStatut.EN_ATTENTE:
        return 'En attente';
      case FebStatut.VALIDEE:
        return 'Validee';
      case FebStatut.ARCHIVEE:
        return 'Archivee';
      default:
        return '-';
    }
  }

  private greyGrid(): any {
    return {
      hLineWidth: () => 0.7,
      vLineWidth: () => 0.7,
      hLineColor: () => '#CCCCCC',
      vLineColor: () => '#CCCCCC'
    };
  }

  private getBase64ImageFromURL(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const context = canvas.getContext('2d');
          if (!context) {
            reject(new Error('Canvas non disponible'));
            return;
          }
          context.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = () => reject(new Error(`Impossible de charger le logo (${url})`));
      img.src = url;
    });
  }

  private sanitizeFileName(value: string): string {
    return value.trim().replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, '-');
  }
}
